import { useState, useCallback } from 'react';

/**
 * Generic API hook for the ATH Okapi API.
 * * fetchData(endpoint, page, pageSize) performs GET and tries to extract the first array it finds in the response body.
 * * postData(endpoint, payload) sends JSON POST and returns JSON.
 * * putData(endpoint, payload) sends JSON PUT and returns JSON.
 * * uploadData(endpoint, formData) sends multipart/form-data POST (no Content-Type header).
 * * deleteData(endpoint) sends DELETE.
 *
 * Note: endpoint should start with a leading slash, e.g. "/languages" or "/resources/translation-memory".
 */
export default function useApiFetch(apiUrl) {
    const [data, setData] = useState([]);       // last received array-like payload
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const _extractFirstArray = useCallback((json) => {
        if (!json) return [];
        if (Array.isArray(json)) return json;
        const firstArrayKey = Object.keys(json).find((k) => Array.isArray(json[k]));
        return firstArrayKey ? json[firstArrayKey] : [];
    }, []);

    const fetchData = useCallback(async (endpoint = '/', page = 1, pageSize = 100) => {
        setLoading(true);
        setError(null);
        try {
            const url = new URL(apiUrl + endpoint);
            if (page !== undefined && pageSize !== undefined) {
                url.searchParams.set('page', String(page));
                url.searchParams.set('page_size', String(pageSize));
            }
            const res = await fetch(url.toString());
            if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
            const json = await res.json();
            const arr = _extractFirstArray(json);
            setData(arr || []);
            return json;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [apiUrl, _extractFirstArray]);

    const postData = useCallback(async (endpoint, payload) => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(apiUrl + endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
            return await res.json();
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [apiUrl]);

    const putData = useCallback(async (endpoint, payload) => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(apiUrl + endpoint, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
            return await res.json();
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [apiUrl]);

    const uploadData = useCallback(async (endpoint, formData) => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(apiUrl + endpoint, {
                method: 'POST',
                body: formData, // browser sets Content-Type multipart/form-data boundary
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
            // Some endpoints return 201 with no JSON body; handle gracefully
            const text = await res.text();
            try {
                return text ? JSON.parse(text) : {};
            } catch (e) {
                return {};
            }
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [apiUrl]);

    const deleteData = useCallback(async (endpoint) => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(apiUrl + endpoint, { method: 'DELETE' });
            if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
            return true;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [apiUrl]);

    return { data, setData, loading, error, fetchData, postData, putData, uploadData, deleteData };
}